import type { ViewRecordFieldChange } from '@salescore/client-redux'
import {
  assignInnerRowGeometry,
  type CompiledFormViewConfig,
  getVisibleNodeNames,
  type ModelSearcher,
  type ViewQueryList,
  type ViewQueryNode,
  type ViewQueryRecordNode,
} from '@salescore/core'
import { stableStringify } from '@salescore/features'
import { logger } from '@salescore/frontend-common'
import { App } from 'antd'
import { Array, Equivalence, Filter, HashMap, Option, pipe } from 'effect'
import { isNotNullish, isNotUndefined } from 'effect/Predicate'
import { t } from 'i18next'
import { useCallback } from 'react'
import { useSetRecoilState } from 'recoil'

import { useUpsertViewRecordsEntry } from '../../../hooks/useUpsertViewRecordsEntry'
import { isNewRecord } from '../../../rsheet/util/isNewRecord'
import { generateNewRecordId, getNodePure } from '../../../state/nodeUtility'
import { recordIdsAtom, recordsImplAtom, useRecordsImpl } from '../../records'
import { changesAtom } from '../../recordsHistory'
import { rowConfigsImplAtom } from '../../rowConfig'
import type { SalescoreObjectAbility, ViewAbility } from '../../view/atoms'
import { useModelSearcher } from '../../view/selectors/modelSearcherSelector'
import type { FeedbackMessage } from '../atoms'
import { getDefaultAttributes } from '../selectors/querySelector'
import { generateDeleteChangesPure } from './upsertViewRecords/generateDeleteChanges'
import { useEvaluateFieldCallbacks } from './useEvaluateFieldCallbacks'

interface RecordNodePathAtom {
  tableNodeName: string
  recordNodeId: string
}

export type RecordNodePath = RecordNodePathAtom[]

interface RecordChangeByPath {
  recordNodePath: RecordNodePath
  id: string
  fieldName: string
  value: unknown
}

//
// recordsを破壊的操作で更新し、変更結果changesを返す関数
// eslint-disable-next-line sonarjs/todo-tag
// TODO: 新規作成系は既にaddEmptyRecordMutation関連で実装しており、ここで作成することはペースト系のみしかないはず。
//       ペーストのロジックを書き換え次第、ここのロジックをリファクタリングする。
//
export function upsertViewRecordByPath({
  record,
  view,
  recordChanges,
}: {
  record: ViewQueryRecordNode
  view: ViewQueryList
  recordChanges: RecordChangeByPath[]
}): { changes: ViewRecordFieldChange[]; feedbackMessages: FeedbackMessage[] } {
  const upsertResults = Array.map(recordChanges, (recordChange) => upsertViewRecord(recordChange, record, view))
  const changes = Array.flatMap(upsertResults, (x) => x.changes)
  const feedbackMessages = pipe(
    upsertResults,
    Array.map((x) => x.feedbackMessage),
    Array.filter(isNotUndefined),
  )
  return { changes, feedbackMessages }
}

export function upsertViewRecord(
  recordChange: RecordChangeByPath,
  rootRecordNode: ViewQueryRecordNode,
  query: ViewQueryList,
): { changes: ViewRecordFieldChange[]; feedbackMessage?: FeedbackMessage } {
  const { recordNodePath, fieldName, value } = recordChange
  const field = query.fields.find((x) => x.name === fieldName)
  if (field?.write === undefined) {
    return { changes: [] }
  }

  // レコードの中の対象ノードを取得
  // 親要素の時レコード、子の時はレコードの配列
  const targetRecordNode = getRecordNodeFromRecordNodePath(rootRecordNode, recordNodePath)

  // 対象ノードが存在しないことはありえないが、一応ガードしておく
  if (targetRecordNode === undefined) {
    return { changes: [] }
  }

  const oldValue = targetRecordNode.attributes[field.name]
  if (oldValue === value) {
    return { changes: [] }
  }

  if (field.meta.creatable === false && isNewRecord(targetRecordNode)) {
    return {
      changes: [],
      feedbackMessage: {
        message: t(`{{label}}は作成禁止です`, { label: field.meta.label }),
        type: 'warn' as const,
      },
    }
  }
  if (field.meta.updatable === false && !isNewRecord(targetRecordNode)) {
    return {
      changes: [],
      feedbackMessage: {
        message: t(`{{label}}は更新禁止です`, { label: field.meta.label }),
        type: 'warn' as const,
      },
    }
  }

  // レコードを更新
  targetRecordNode.attributes[field.name] = value

  if (targetRecordNode.id === undefined) {
    return { changes: [] }
  }
  const nameField = query.fields
    .filter((x) => Array.makeEquivalence(Equivalence.String)(x.nodePath, query.tree.path))
    .find((field) => field.meta.fieldMetaType === 'name')
  return {
    changes: [
      {
        rootNodeId: rootRecordNode.id,
        id: targetRecordNode.id,
        streamName: field.write.streamName,
        fieldChanges: [
          {
            id: targetRecordNode.id,
            fieldName: field.name,
            propertySourceName: field.write.propertySourceName,
            value,
            // eslint-disable-next-line sonarjs/todo-tag
            rowIndex: 0, // TODO
            // eslint-disable-next-line sonarjs/todo-tag
            innerRowIndex: 0, // TODO
          },
        ],
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
        recordNameLabel: nameField === undefined ? undefined : (targetRecordNode.attributes[nameField.name] as string),
        before: {
          [field.write.propertySourceName]: oldValue,
        },
        after: {
          [field.write.propertySourceName]: value,
        },
      },
    ],
  }
}

export function getRecordNodeFromRecordNodePath(
  recordNode: ViewQueryRecordNode,
  recordNodePath: RecordNodePath,
): ViewQueryRecordNode | undefined {
  if (recordNodePath.length === 0) {
    return recordNode
  }
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const { tableNodeName, recordNodeId } = recordNodePath[0]!
  const tableNode = recordNode.children.find((x) => x.nodeName === tableNodeName)
  const childRecordNode = (tableNode?.children ?? []).find((x) => x.id === recordNodeId)
  if (childRecordNode === undefined) {
    return undefined
  }
  return getRecordNodeFromRecordNodePath(childRecordNode, recordNodePath.slice(1))
}

interface NewRecordChangeByPath {
  recordNodePath: RecordNodePath
  tableNodeName: string
  insertIndex: number
}

export function addNewChildRecordByPath(
  query: ViewQueryList,
  rootRecordNode: ViewQueryRecordNode,
  newRecordChange: NewRecordChangeByPath,
): Option.Option<{
  recordNode: ViewQueryRecordNode
  recordChangesForDefault: RecordChangeByPath[]
  change: ViewRecordFieldChange | undefined
}> {
  const { tree } = query
  const { recordNodePath, tableNodeName, insertIndex } = newRecordChange

  const targetRecordNode = getRecordNodeFromRecordNodePath(rootRecordNode, recordNodePath)
  if (targetRecordNode === undefined) {
    return Option.none()
  }

  const nodePath = [tree.name, ...recordNodePath.map((x) => x.tableNodeName), tableNodeName]
  const targetQueryNode = getNodePure(tree, nodePath)
  if (targetQueryNode === undefined) {
    return Option.none()
  }

  const targetTableNode = targetRecordNode.children.find((x) => x.nodeName === tableNodeName)
  if (targetTableNode === undefined) {
    return Option.none()
  }

  // 新規レコードを生成
  const recordNode = generateNewChildRecord(targetQueryNode, targetRecordNode.id).newRecord

  // 新規レコードを挿入
  targetTableNode.children.splice(insertIndex, 0, recordNode)

  const { defaultValues } = getDefaultAttributes(targetQueryNode, query)
  const recordChangesForDefault = defaultValues.map(({ field, value }): RecordChangeByPath => ({
    recordNodePath: [...recordNodePath, { tableNodeName: targetTableNode.nodeName, recordNodeId: recordNode.id }],
    id: recordNode.id,
    fieldName: field.name,
    value,
  }))

  // 変更差分を生成
  return Option.some({
    recordNode,
    recordChangesForDefault,
    change: generateNewChildRecordChange(rootRecordNode, targetQueryNode, recordNode, targetRecordNode),
  })
}

/**
 * newRecordChanges を順に rootRecordNode へ挿入し、挿入できたものだけを返す (rootRecordNode を直接書き換える)
 */
export const applyNewRecordChangesByPath = (
  query: ViewQueryList,
  rootRecordNode: ViewQueryRecordNode,
  newRecordChanges: NewRecordChangeByPath[],
): Array<{
  recordNode: ViewQueryRecordNode
  recordChangesForDefault: RecordChangeByPath[]
  change: ViewRecordFieldChange | undefined
}> =>
  Array.filterMap(
    newRecordChanges,
    Filter.fromPredicateOption((x) => addNewChildRecordByPath(query, rootRecordNode, x)),
  )

export function generateNewChildRecord(node: ViewQueryNode, parentRecordId: string | undefined) {
  // changesの生成
  const relationColumn =
    node.read.join?.joinOn.type === 'sql' ? undefined : node.read.join?.joinOn.meta.currentNodePropertyName

  // const defaultAttributes = getDefaultAttributes(node, view) // デフォルト系は別途求める
  // レコードの作成
  const id = generateNewRecordId()
  const newRecord: ViewQueryRecordNode = {
    id,
    attributes: {
      // ...defaultAttributes.recordAttributes,
      // ...attributes,
      ...(relationColumn !== undefined && parentRecordId !== undefined
        ? {
            // relationColumnは厳密にはattributesでないのでrecordNodeに含める必要はないが、changesのみに存在するのが分かりづらいためいれておく。
            // サーバーサイドのレスポンスも同様になっている。
            [`${node.name}_${relationColumn}`]: parentRecordId,
          }
        : {}),
    },
    children: [],
    meta: {
      height: 1,
      // eslint-disable-next-line sonarjs/todo-tag
      innerRowIndexStart: 0, // TODO
      innerRowIndexEnd: 1,
    },
  }
  const changes: ViewRecordFieldChange[] = [
    {
      id,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      streamName: node.write!.streamName,
      fieldChanges: [],
      // recordNameLabel,
      before: {},
      after: {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        [relationColumn!]: parentRecordId,
      },
    },
  ]

  return {
    newRecord,
    changes,
  }
}

export function generateNewChildRecordChange(
  rootRecordNode: ViewQueryRecordNode,
  node: ViewQueryNode,
  recordNode: ViewQueryRecordNode,
  parentRecord: ViewQueryRecordNode | undefined,
): ViewRecordFieldChange | undefined {
  const joinOn = node.read.join?.joinOn
  if (joinOn === undefined || joinOn.type === 'sql') {
    return
  }
  const relationColumn = joinOn.meta.currentNodePropertyName
  if (node.write?.streamName === undefined || parentRecord === undefined) {
    return
  }

  return {
    rootNodeId: rootRecordNode.id,
    id: recordNode.id,
    streamName: node.write.streamName,
    fieldChanges: [],
    before: {},
    after: {
      [relationColumn]: parentRecord.id,
    },
  }
}

interface DeleteRecordChangeByPath {
  recordNodePath: RecordNodePath
  isDeleteAllChildren?: boolean
}

export function deleteRecordByPath(
  query: ViewQueryList,
  rootRecordNode: ViewQueryRecordNode,
  newRecordChange: DeleteRecordChangeByPath,
  modelSearcher: ModelSearcher,
): { changes: ViewRecordFieldChange[]; deletedRootRecordId?: string } {
  const { recordNodePath, isDeleteAllChildren } = newRecordChange

  if (recordNodePath.length === 0) {
    return {
      changes: generateDeleteChangesPure(query, query.tree, rootRecordNode, modelSearcher, isDeleteAllChildren),
      deletedRootRecordId: rootRecordNode.id,
    }
  }

  const parentPath = recordNodePath.slice(0, -1)
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const { tableNodeName, recordNodeId } = recordNodePath.at(-1)!
  const targetRecordNode = getRecordNodeFromRecordNodePath(rootRecordNode, parentPath)
  if (targetRecordNode === undefined) {
    return { changes: [] }
  }

  const nodePath = [query.tree.name, ...recordNodePath.map((x) => x.tableNodeName)]
  const targetQueryNode = getNodePure(query.tree, nodePath)
  if (targetQueryNode === undefined) {
    return { changes: [] }
  }

  const targetTableNode = targetRecordNode.children.find((x) => x.nodeName === tableNodeName)
  if (targetTableNode === undefined) {
    return { changes: [] }
  }

  const deleteIndex = targetTableNode.children.findIndex((x) => x.id === recordNodeId)
  const childRecordNode = targetTableNode.children[deleteIndex]
  if (childRecordNode === undefined) {
    return { changes: [] }
  }

  targetTableNode.children.splice(deleteIndex, 1)

  // 変更差分を返す
  return {
    changes: generateDeleteChangesPure(query, targetQueryNode, childRecordNode, modelSearcher, isDeleteAllChildren),
  }
}

// upsertViewRecordsを実行し、その結果をsetするmutation
export const useSetUpsertViewRecordsByPath = (
  compiledConfig: CompiledFormViewConfig,
  ability: ViewAbility & SalescoreObjectAbility,
): (({
  recordChanges,
  newRecordChanges,
  deleteRecordChanges,
}: {
  newRecordChanges?: NewRecordChangeByPath[]
  recordChanges?: RecordChangeByPath[]
  deleteRecordChanges?: DeleteRecordChangeByPath[]
}) => void) => {
  const records = useRecordsImpl()
  const modelSearcher = useModelSearcher()

  const setRowConfigsImpl = useSetRecoilState(rowConfigsImplAtom)
  const { message } = App.useApp()
  const setRecordIds = useSetRecoilState(recordIdsAtom)
  const setRecordsImpl = useSetRecoilState(recordsImplAtom)
  const setChanges = useSetRecoilState(changesAtom)

  const query = compiledConfig.queries[0]
  const visibleNodeNames = getVisibleNodeNames(query)
  const evaluateFieldCallbacks = useEvaluateFieldCallbacks(query)
  const upsertViewRecordsEntry = useUpsertViewRecordsEntry(query, ability)

  return useCallback(
    ({ recordChanges, newRecordChanges, deleteRecordChanges }): void => {
      // レコードをcloneして参照渡しで更新していく
      // このような形にしないと、前の関数での変更が次の関数に伝わらない。また、関数の中でsetする際にレコードを普通に渡すとfreezeされるので、cloneして渡すこと。
      const newRecords = structuredClone(records)

      const rootRecordNode = newRecords[0]
      if (rootRecordNode === undefined) {
        return
      }
      const rootRecordId = rootRecordNode.id

      const newChanges = applyNewRecordChangesByPath(query, rootRecordNode, newRecordChanges ?? [])
      if (Array.isArrayNonEmpty(newChanges)) {
        setRowConfigsImpl((previous) => HashMap.set(previous, rootRecordId, { expanded: true }))
      }

      const { changes: upsertedChanges, feedbackMessages } = upsertViewRecordByPath({
        record: rootRecordNode,
        view: query,
        recordChanges: [...newChanges.flatMap((x) => x.recordChangesForDefault), ...(recordChanges ?? [])],
      })
      // 最後のメッセージしか反映されないが現行の動作に合わせている
      if (Array.isArrayNonEmpty(feedbackMessages)) {
        // eslint-disable-next-line sonarjs/todo-tag
        // TODO: feedbackMessagesAtomの名残でエラー時のフィードバックの責務が混入していて良くない
        void message.warning(Array.lastNonEmpty(feedbackMessages).message)
      }

      const deleteResults = Array.map(deleteRecordChanges ?? [], (x) =>
        deleteRecordByPath(query, rootRecordNode, x, modelSearcher),
      )

      pipe(
        deleteResults,
        Array.map((r) => r.deletedRootRecordId),
        Array.filter(isNotNullish),
        Array.match({
          onEmpty: () => {
            // empty
          },
          onNonEmpty: (ids) => {
            setRecordIds((oldIds) => Array.difference(oldIds, ids))
          },
        }),
      )

      const deleteChanges = Array.flatMap(deleteResults, (x) => x.changes)

      // 区間 (innerRowIndexStart / innerRowIndexEnd) は行 div の高さとセル列の高さの両方が読むので、
      // ツリーの構造変更が全て終わってから 1 度だけ確定させる。追加・削除の各関数が個別に確定させると、
      // 途中の値が最終結果に影響しないままツリーを作り直すことになる。
      const updatedRootRecordNode = assignInnerRowGeometry(rootRecordNode, visibleNodeNames)
      const updatedRecords = newRecords.toSpliced(0, 1, updatedRootRecordNode)

      setRecordsImpl((previous) => HashMap.set(previous, updatedRootRecordNode.id, updatedRootRecordNode))

      //
      // changesを反映させる
      //
      const changes = [...newChanges.map((x) => x.change), ...upsertedChanges, ...deleteChanges].filter(isNotNullish)
      if (changes.length > 0) {
        const newChange = {
          datetime: new Date().toISOString(),
          // eslint-disable-next-line sonarjs/todo-tag
          rowIndices: [0], // TODO: 結局使ってないので廃止
          snapshot: JSON.stringify(records), // snapshotは以前のものをいれる
          snapshotAfterChange: JSON.stringify(updatedRecords),
          changes,
        }
        setChanges((oldChanges) => [...oldChanges, newChange])
        evaluateFieldCallbacks(newChange)
          .then((recordChanges) =>
            upsertViewRecordsEntry({
              recordChanges,
              recordsAfterHeightAdjustment: updatedRecords,
            }),
          )
          .catch(() => {
            logger.warn(
              'SHEET_FIELD_CALLBACK',
              `フィールドに設定されたコールバックの呼び出し中に失敗しました。\nchanges=${stableStringify(newChange.changes)}`,
            )
          })
      }
    },
    [
      records,
      modelSearcher,
      query,
      visibleNodeNames,
      evaluateFieldCallbacks,
      setChanges,
      setRecordIds,
      setRecordsImpl,
      setRowConfigsImpl,
      message,
      upsertViewRecordsEntry,
    ],
  )
}
